import React, { useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Mail, Send, MessageCircle, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SITE } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const schema = z.object({
  name: z.string().min(2, "Please share your name (at least 2 characters)."),
  email: z.string().email("That doesn't look like a valid email."),
  service: z.string().optional(),
  message: z.string().min(10, "Tell us a little more (at least 10 characters)."),
});

const SERVICE_OPTIONS = [
  { value: "custom", label: "Custom Website" },
  { value: "waas", label: "Website as a Service" },
  { value: "hosting", label: "Hosting & Maintenance" },
  { value: "not-sure", label: "Not sure yet — just want to chat" },
];

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", service: "", message: "" },
  });

  const currentService = watch("service");

  const onSubmit = async (data) => {
    try {
      const payload = { ...data };
      if (!payload.service) delete payload.service;
      const res = await fetch(`${API}/webform/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 503 = service not configured, 502/500 = azure error
        const msg =
          res.status === 503
            ? "Our mailbox isn't quite ready yet. Please email us directly at info@threeballs.digital."
            : json?.detail || "Something went sideways. Please try again in a moment.";
        toast.error(msg);
        return;
      }
      toast.success(json?.message || "Thanks! We'll be in touch soon.");
      setSubmitted(true);
      reset();
    } catch (err) {
      toast.error("Couldn't reach the server. Please check your connection and try again.");
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-36 bg-[#FDFCF9]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl md:text-6xl lg:text-[76px] leading-[0.95] text-[#1A1A1A]">
            Say{" "}
            <span className="text-[#E9298A]">hi</span>.
            <br />
            No pitch. Just talk.
          </h2>
          <p className="mt-6 text-lg text-[#1A1A1A]/75 max-w-md leading-relaxed">
            Tell us about your business (or your headache). We'll reply within 24 hours
            with actual humans and actual answers.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href="mailto:info@threeballs.digital"
              className="flex items-center gap-3 group"
            >
              <span className="w-11 h-11 rounded-full border border-[#1A1A1A] flex items-center justify-center bg-white group-hover:bg-[#E9298A] group-hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
              </span>
              <div>
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">
                  Email
                </div>
                <div className="font-semibold text-[#1A1A1A]">info@threeballs.digital</div>
              </div>
            </a>
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full border border-[#1A1A1A] flex items-center justify-center bg-white">
                <MessageCircle className="w-4 h-4" />
              </span>
              <div>
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">
                  Reply time
                </div>
                <div className="font-semibold text-[#1A1A1A]">Usually within 24 hours</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-white border border-[#1A1A1A] rounded-[28px] p-6 md:p-10 shadow-[6px_6px_0_#1A1A1A]"
          >
            {submitted ? (
              <div className="flex flex-col items-center text-center py-8">
                <div className="w-16 h-16 rounded-full bg-[#FFE600] border border-[#1A1A1A] flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-[#1A1A1A]" strokeWidth={2.5} />
                </div>
                <h3 className="mt-6 font-display font-black text-3xl md:text-4xl tracking-tight text-[#1A1A1A]">
                  Message sent!
                </h3>
                <p className="mt-3 text-[#1A1A1A]/75 max-w-md">
                  Thanks for reaching out — we'll be in touch within 24 hours with an
                  actual human reply. No auto-responder nonsense.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-8 tb-pill tb-pill-ghost"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form
                data-testid={SITE.contactForm}
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-5"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="tb-name" className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#1A1A1A]/70">
                      Name *
                    </Label>
                    <Input
                      id="tb-name"
                      data-testid={SITE.contactName}
                      placeholder="Alex Chan"
                      className="mt-2 h-12 rounded-xl border-[#1A1A1A]/30 focus-visible:ring-[#E9298A] focus-visible:ring-2"
                      {...register("name")}
                    />
                    {errors.name && (
                      <p className="mt-1.5 text-xs text-[#E9298A]">{errors.name.message}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="tb-email" className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#1A1A1A]/70">
                      Email *
                    </Label>
                    <Input
                      id="tb-email"
                      type="email"
                      data-testid={SITE.contactEmail}
                      placeholder="you@business.hk"
                      className="mt-2 h-12 rounded-xl border-[#1A1A1A]/30 focus-visible:ring-[#E9298A] focus-visible:ring-2"
                      {...register("email")}
                    />
                    {errors.email && (
                      <p className="mt-1.5 text-xs text-[#E9298A]">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#1A1A1A]/70">
                    I'm interested in
                  </Label>
                  <Select
                    value={currentService || ""}
                    onValueChange={(v) => setValue("service", v, { shouldValidate: true })}
                  >
                    <SelectTrigger
                      data-testid={SITE.contactService}
                      className="mt-2 h-12 rounded-xl border-[#1A1A1A]/30 focus:ring-[#E9298A] focus:ring-2"
                    >
                      <SelectValue placeholder="Pick a service (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`contact-service-option-${o.value}`}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="tb-message" className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#1A1A1A]/70">
                    Your message *
                  </Label>
                  <Textarea
                    id="tb-message"
                    data-testid={SITE.contactMessage}
                    rows={5}
                    placeholder="Tell us about your business, what you need, and any deadlines you're working with."
                    className="mt-2 rounded-xl border-[#1A1A1A]/30 focus-visible:ring-[#E9298A] focus-visible:ring-2"
                    {...register("message")}
                  />
                  {errors.message && (
                    <p className="mt-1.5 text-xs text-[#E9298A]">{errors.message.message}</p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-[#1A1A1A]/60 max-w-xs">
                    By sending this, you agree we can email you back. We won't spam you. Promise.
                  </p>
                  <button
                    type="submit"
                    data-testid={SITE.contactSubmit}
                    disabled={isSubmitting}
                    className="tb-pill tb-pill-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Sending..." : "Send message"}
                    <span className="w-7 h-7 -mr-1 rounded-full bg-[#FFE600] text-[#1A1A1A] flex items-center justify-center">
                      <Send className="w-3.5 h-3.5" />
                    </span>
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
